import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import coverImg from "./../../images/blog/capacity-planning-ai-agents.svg";
import modelImg from "./../../images/blog/capacity-planning-ai-agents-model.svg";
import FAQ from "../../components/FAQs";
import { DEMO_LINK } from "../../components/demoBooking";
import CopyLinkButton from "../../components/CopyLinkButton";

/**
 * Capacity Planning When Part of Your Team Is Software.
 * Original, SEO-focused content with verifiable, currently-live references.
 */

export default function Blog_CapacityPlanning() {
  const title =
    "Capacity Planning When Part of Your Team Is Software";
  const description =
    "Headcount plans cannot model agent capacity. A blended capacity model for 2027: how to size usable human hours, agent throughput, and the escalation reserve.";
  const canonicalUrl = "https://bossdrop.ai/blog/capacity-planning-ai-agents";
  const published = "2026-10-19";
  const readTime = "12 min read";

  // SEO keyword cluster (base BossDrop terms + capacity-planning intent)
  const keywords = [
    "bossdrop",
    "bossdrop ai",
    "bossdrop.ai",
    "bossdrop company",
    "ai project management",
    "agentic automation",
    "ai workflow automation",
    "work management",
    "coordination overhead",
    "ai agent automation",
    "capacity planning",
    "capacity planning with ai agents",
    "blended workforce planning",
    "digital labor capacity",
    "agent capacity model",
    "headcount planning",
    "workforce planning ai",
    "resource planning software",
    "how to measure ai agent capacity",
    "how do you budget for ai agents",
    "do ai agents count as headcount",
    "usable hours vs headcount",
    "team utilization planning",
    "quarterly planning cycle",
    "2027 planning",
    "annual operating plan",
    "escalation rate",
    "agent throughput",
    "human in the loop approvals",
    "ai agent governance",
    "ai roi measurement",
    "operations leadership",
  ];

  const shareUrl = encodeURIComponent(canonicalUrl);
  const shareText = encodeURIComponent(title);
  const onShare = (net) => {
    const map = {
      x: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    document.title = title;
    setCanonical(canonicalUrl);
    setMeta(description);
    setKeywords(keywords.join(", "));
    setOG("og:type", "article");
    setOG("og:title", title);
    setOG("og:description", description);
    setOG("og:url", canonicalUrl);
    setOG("og:image", toAbs(coverImg));
    setOG("twitter:card", "summary_large_image");
    setOG("twitter:title", title);
    setOG("twitter:description", description);
    setOG("twitter:image", toAbs(coverImg));

    injectLDJSON("ldjson-blogpost", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: title,
      description,
      datePublished: published,
      dateModified: published,
      author: { "@type": "Organization", name: "BossDrop" },
      publisher: {
        "@type": "Organization",
        name: "BossDrop",
        logo: { "@type": "ImageObject", url: "https://bossdrop.ai/icon.png" },
      },
      image: toAbs(coverImg),
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      keywords: keywords.join(", "),
      articleSection: "Operations",
    });

    injectLDJSON("ldjson-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is capacity planning for AI agents?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Capacity planning for AI agents means sizing the work an agent can actually complete in a period, in the same plan you use for people. People are sized in usable hours after meetings and unplanned work. Agents are sized in throughput: runs per day times tasks per run, minus the share that escalates to a human. Both feed one capacity number for the quarter.",
          },
        },
        {
          "@type": "Question",
          name: "How do you measure the capacity of an AI agent?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Measure three things over two weeks of real use: how many runs the agent completes, how many work items each run closes, and what share of those items a human has to take back. Multiply the first two and subtract the escalation share. That is your net agent throughput. Track cost per completed item alongside it so the capacity number and the spend number move together.",
          },
        },
        {
          "@type": "Question",
          name: "Should AI agents count as headcount in a workforce plan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No. An agent has no seat, so putting it on a headcount line either hides it or turns it into unexplained spend. Plan agents as a second kind of capacity with its own unit, its own cost driver, and its own governance requirements, then roll both lanes into a single capacity ledger for the quarter.",
          },
        },
        {
          "@type": "Question",
          name: "How do you budget for AI agents?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Budget agents as variable cost tied to volume rather than fixed cost tied to a seat. Estimate runs per month, multiply by cost per run, and attach that line to the specific outcome it was funded for. Add a review-time line for the human approvals the agent will generate, because that time is real capacity being consumed.",
          },
        },
        {
          "@type": "Question",
          name: "How is capacity planning different from workforce planning?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Workforce planning looks three to five years out and asks what skills the strategy will need. Capacity planning asks whether the team you have can absorb the demand in front of you this quarter. Agents change capacity planning first, because they can be added in days, and they change workforce planning later, as the skill mix shifts toward review and exception handling.",
          },
        },
        {
          "@type": "Question",
          name: "Does adding AI agents mean we stop hiring?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Not usually. Agents absorb coordination work: status gathering, routing, follow-up, and handoffs. That frees human capacity rather than eliminating the need for it. The decision worth making in planning is where the freed hours go. Capacity that is not explicitly reassigned gets absorbed by whatever is loudest.",
          },
        },
      ],
    });

    // reveal on scroll
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!("IntersectionObserver" in window) || !els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="page container">
      {/* Breadcrumbs */}
      <nav
        className="breadcrumbs reveal"
        style={{ "--delay": "0ms" }}
        aria-label="Breadcrumb"
      >
        <Link to="/">Home</Link>
        <span>›</span>
        <Link to="/blog">Blog</Link>
        <span>›</span>
        <span aria-current="page">Capacity Planning</span>
      </nav>

      {/* Feature hero */}
      <div className="hero-feature reveal" style={{ "--delay": "40ms" }}>
        <div className="hero-gradient" />
        <div className="hero-grid">
          <div className="hero-card">
            <img
              src={coverImg}
              alt="Capacity planning with AI agents: a demand line above fixed human capacity, with an elastic metered agent capacity band closing the gap"
              width="1200"
              height="630"
            />
          </div>

          <div className="hero-text">
            <div className="pill">OPERATIONS</div>
            <h1 className="h1">{title}</h1>

            <div className="byline">
              <div className="author">
                <div className="avatar" aria-hidden="true">
                  BD
                </div>
                <div>
                  <div className="author-name">BossDrop Team</div>
                  <div className="meta">
                    {new Date(published).toLocaleDateString()} • {readTime}
                  </div>
                </div>
              </div>

              <div className="share">
                <button
                  aria-label="Share on X"
                  onClick={() => onShare("x")}
                  title="Share on X"
                >
                  {iconX()}
                </button>
                <button
                  aria-label="Share on LinkedIn"
                  onClick={() => onShare("linkedin")}
                  title="Share on LinkedIn"
                >
                  {iconLinkedIn()}
                </button>
                <button
                  aria-label="Share on Facebook"
                  onClick={() => onShare("facebook")}
                  title="Share on Facebook"
                >
                  {iconFacebook()}
                </button>
                <CopyLinkButton url={canonicalUrl} />
              </div>
            </div>
          </div>
        </div>

        {/* CTA bar */}
        <div className="hero-cta">
          <span>
            A seat is not a unit of capacity. BossDrop gives you the{" "}
            <strong>coordination capacity</strong> your plan is actually short
            of.
          </span>
          <a
            className="btn btnDemo"
            {...DEMO_LINK}
            aria-label="Book a demo"
          >
            Book a demo
          </a>
        </div>
      </div>

      {/* ---- Article ---- */}
      <article className="article">
        <p className="lede reveal" style={{ "--delay": "80ms" }}>
          It is planning season. Somewhere in your company there is a
          spreadsheet with one row per person and one column per quarter, and
          that spreadsheet is about to decide what gets built next year.
          <br />
          <br />
          It has a problem. Part of the work in 2027 will be done by software
          that has no seat, no start date, and no manager. It can be switched on
          in an afternoon and switched off just as fast. It bills by the run
          instead of by the month. A headcount sheet cannot represent it, so
          most plans do one of two things: leave it out, or bury it in a
          software line that nobody connects to delivery. Both produce a plan
          that is wrong in a way nobody notices until Q2.
        </p>

        <nav
          className="toc reveal"
          style={{ "--delay": "100ms" }}
          aria-label="Table of contents"
        >
          <a href="#why-headcount">Why headcount is the wrong unit</a>
          <a href="#freed-capacity">The capacity you already freed</a>
          <a href="#two-units">Two units of capacity, compared</a>
          <a href="#model">How to build a blended capacity model</a>
          <a href="#breaks">Four ways blended plans break</a>
          <a href="#bossdrop">What this looks like in BossDrop</a>
          <a href="#plan">What to put in the plan instead</a>
          <a href="#resources">External resources</a>
          <a href="#faq">FAQ</a>
        </nav>

        <h2 id="why-headcount" className="reveal" style={{ "--delay": "120ms" }}>
          Why headcount is the wrong unit for capacity planning
        </h2>
        <p className="reveal" style={{ "--delay": "140ms" }}>
          Headcount answers a budget question: how many salaries are we paying.
          Capacity answers a delivery question: how much work can we absorb.
          Those were never the same number, and the gap between them is not
          small. Asana&apos;s Anatomy of Work research has repeatedly put the
          share of the day spent on{" "}
          <a
            href="https://asana.com/resources/anatomy-of-work-index"
            target="_blank"
            rel="noopener noreferrer"
          >
            coordination rather than skilled work
          </a>{" "}
          at well over half. Ten people on an org chart were never ten people of
          usable capacity.
        </p>
        <p className="reveal" style={{ "--delay": "150ms" }}>
          Planners know this, and they still plan from seats, because seats are
          the number finance agrees on. The cost of that shortcut shows up in
          execution. In Tempo&apos;s{" "}
          <a
            href="https://www.tempo.io/guides/2026-state-of-spm-report-guide"
            target="_blank"
            rel="noopener noreferrer"
          >
            2026 State of SPM report
          </a>
          , a survey of 667 planning and PMO leaders across 43 countries,
          capacity planning ranked as the number one barrier to strategy
          execution, ahead of both prioritization and budget. The same report
          found only 37% of leaders say they have good or complete visibility
          across projects, and 46% of organizations review plans only quarterly
          or annually.
        </p>
        <p className="reveal" style={{ "--delay": "160ms" }}>
          So the starting condition is a plan built on an inflated unit,
          reviewed rarely, with partial visibility into what the teams are
          actually carrying. Now add a second kind of worker that the unit
          cannot express at all.
        </p>

        <div className="panel reveal" style={{ "--delay": "170ms" }}>
          <p>
            <strong>Usable capacity</strong> = FTE × available hours × (1 −
            coordination load).
          </p>
          <p className="muted">
            A twelve-person team at 1,900 available hours each, carrying a 50%
            coordination load, has roughly 11,400 usable hours a year, not
            22,800. Every plan built on the second number is a plan that will be
            missed.
          </p>
        </div>

        <h2
          id="freed-capacity"
          className="reveal"
          style={{ "--delay": "180ms" }}
        >
          The capacity you already freed and never planned for
        </h2>
        <p className="reveal" style={{ "--delay": "200ms" }}>
          Here is the finding that should change how you plan this quarter. In
          BCG&apos;s fourth annual{" "}
          <a
            href="https://www.bcg.com/publications/2026/ai-at-work-why-strategy-matters-more-than-tools"
            target="_blank"
            rel="noopener noreferrer"
          >
            AI at Work study, published in June 2026
          </a>
          , 74% of frontline employees are now regular AI users, and 42% of
          those regular users report saving roughly a full workday each week.
          That is a large amount of capacity, already created, already paid for.
        </p>
        <p className="reveal" style={{ "--delay": "210ms" }}>
          The same study found that 66% of those employees received limited or no
          guidance on what to do with the time, and more than half did not
          redirect it into higher-value work. Capacity that nobody assigns does
          not sit in a bank waiting for the planning cycle. It gets absorbed by
          whatever is loudest: more meetings, more Slack, a longer queue of small
          requests. The hours were real. The plan never claimed them, so they
          are gone.
        </p>

        <div className="callout info reveal" style={{ "--delay": "220ms" }}>
          <strong>Freed time is not capacity until a plan claims it.</strong>{" "}
          Before you model next year&apos;s agent capacity, find the capacity
          you created this year and write down where it went. If you cannot
          answer that, adding more will not help.
        </div>

        <p className="reveal" style={{ "--delay": "230ms" }}>
          Microsoft&apos;s{" "}
          <a
            href="https://www.microsoft.com/en-us/worklab/work-trend-index/2025-the-year-the-frontier-firm-is-born"
            target="_blank"
            rel="noopener noreferrer"
          >
            2025 Work Trend Index
          </a>{" "}
          names the underlying tension directly: 53% of leaders say productivity
          must increase, while 80% of the global workforce says it lacks the
          time or energy to do the work it already has. Microsoft calls the
          difference a capacity gap, and reports that 82% of leaders expect to
          use digital labor to close it within 12 to 18 months. The intent is
          nearly universal. The planning discipline is not.
        </p>

        <h2 id="two-units" className="reveal" style={{ "--delay": "240ms" }}>
          Two units of capacity: how people and agents actually differ
        </h2>
        <p className="reveal" style={{ "--delay": "250ms" }}>
          A human and an agent are not the same unit scaled differently. They
          fail differently, cost differently, and ramp differently. Planning
          them with one unit misprices both.
        </p>

        <div className="panel reveal" style={{ "--delay": "260ms" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>
                  Dimension
                </th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>
                  Human capacity
                </th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>
                  Agent capacity
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "8px 10px" }}>Unit</td>
                <td style={{ padding: "8px 10px" }}>Usable hours per period</td>
                <td style={{ padding: "8px 10px" }}>
                  Completed items per period
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px 10px" }}>Shape</td>
                <td style={{ padding: "8px 10px" }}>Fixed and discrete</td>
                <td style={{ padding: "8px 10px" }}>Elastic and metered</td>
              </tr>
              <tr>
                <td style={{ padding: "8px 10px" }}>Ramp</td>
                <td style={{ padding: "8px 10px" }}>Weeks to months</td>
                <td style={{ padding: "8px 10px" }}>Days, then weeks of tuning</td>
              </tr>
              <tr>
                <td style={{ padding: "8px 10px" }}>Cost driver</td>
                <td style={{ padding: "8px 10px" }}>Salary, fixed per period</td>
                <td style={{ padding: "8px 10px" }}>Per run, variable with volume</td>
              </tr>
              <tr>
                <td style={{ padding: "8px 10px" }}>What shrinks it</td>
                <td style={{ padding: "8px 10px" }}>
                  Meetings, incidents, unplanned work
                </td>
                <td style={{ padding: "8px 10px" }}>
                  Bad data, missing permissions, review backlog
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px 10px" }}>Hidden cost</td>
                <td style={{ padding: "8px 10px" }}>Context switching</td>
                <td style={{ padding: "8px 10px" }}>
                  Human review time it generates
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px 10px" }}>Failure mode</td>
                <td style={{ padding: "8px 10px" }}>Burnout, attrition</td>
                <td style={{ padding: "8px 10px" }}>
                  Confident wrong output at volume
                </td>
              </tr>
            </tbody>
          </table>
          <p className="muted">
            The last row is why the governance step in the model below is not
            optional. A person who is overloaded slows down. An agent that is
            wrong speeds up.
          </p>
        </div>

        <h2 id="model" className="reveal" style={{ "--delay": "280ms" }}>
          How to build a blended capacity model in an afternoon
        </h2>
        <p className="reveal" style={{ "--delay": "290ms" }}>
          You do not need a new system for this. You need six numbers and the
          discipline to keep them in one place.
        </p>

        <figure className="reveal" style={{ "--delay": "300ms" }}>
          <img
            src={modelImg}
            alt="A blended capacity model diagram: demand splits into a human lane measured in usable hours and an agent lane measured in runs and escalation rate, both passing through a governance gate into one capacity ledger with a reserve for escalations"
            style={{ width: "100%", height: "auto", borderRadius: 14 }}
            width="1040"
            height="672"
            loading="lazy"
          />
          <figcaption className="muted" style={{ marginTop: 8 }}>
            One plan, two units. The governance gate is where agent throughput
            becomes capacity you can count on instead of output you have to
            check.
          </figcaption>
        </figure>

        <ol className="reveal numbered" style={{ "--delay": "320ms" }}>
          <li>
            <strong>Start from demand, not supply.</strong> List the outcomes
            the quarter actually requires and size each one in work items or
            hours. If you cannot size it, you cannot staff it with either kind
            of worker.
          </li>
          <li>
            <strong>Compute usable human hours.</strong> Take available hours
            per person, subtract your real coordination load, and use that
            number. Measure the load once instead of guessing it: two weeks of
            calendar and task data is enough to get within a few points.
          </li>
          <li>
            <strong>Measure agent throughput from real runs.</strong> Net
            throughput is runs per period × items per run × (1 − escalation
            rate). The escalation rate is the share of items a human has to take
            back. Do not estimate it. Run the agent for two weeks and count.
          </li>
          <li>
            <strong>Budget the review time the agent creates.</strong> Every
            escalation and every approval consumes human capacity. If an agent
            closes 400 items a month and escalates 12%, somebody is reviewing 48
            items. Put those hours in the plan, in the human lane, where they
            belong.
          </li>
          <li>
            <strong>Price each lane in its own currency.</strong> Humans in
            salary per period, agents in cost per completed item. Then tie both
            to the outcome they were funded for, so the question &quot;what did
            we get for the AI spend&quot; has an answer that does not require a
            research project.
          </li>
          <li>
            <strong>Re-baseline monthly, not quarterly.</strong> Agent
            throughput moves faster than hiring does. A number you set in
            October and do not touch until January will be wrong by November,
            and wrong in the direction that costs you a quarter.
          </li>
        </ol>

        <h3 className="reveal" style={{ "--delay": "340ms" }}>
          A worked example: a twelve-person operations team
        </h3>
        <p className="reveal" style={{ "--delay": "350ms" }}>
          Twelve people, 150 available hours each per month, so 1,800 hours on
          paper. Measured coordination load is 48%, which leaves 936 usable
          hours. Demand for the quarter needs about 1,150 hours a month. The
          headcount sheet says the team is fine. The capacity model says it is
          roughly 214 hours short every month, which is why the last three
          quarters slipped.
        </p>
        <p className="reveal" style={{ "--delay": "360ms" }}>
          Now add an agent to the coordination work: status roll-ups, chasing
          overdue handoffs, routing requests to owners. Two weeks of real runs
          show it closing 520 coordination items a month with a 14% escalation
          rate, so 447 items land cleanly and 73 come back to a human. Those 73
          cost about 36 hours of review. The coordination load on the team drops
          from 48% to roughly 31%, which lifts usable hours to about 1,242.
          Subtract the 36 review hours and you are at 1,206 against demand of
          1,150. The gap closes without a req, and you can state exactly which
          number you are relying on.
        </p>

        <div className="callout info reveal" style={{ "--delay": "370ms" }}>
          <strong>The point of the model is not the answer.</strong> It is that
          the answer is now falsifiable. If the escalation rate turns out to be
          30% instead of 14%, the plan tells you in week three instead of at the
          quarterly review.
        </div>

        <h2 id="breaks" className="reveal" style={{ "--delay": "380ms" }}>
          Four ways blended capacity plans break
        </h2>
        <p className="reveal" style={{ "--delay": "390ms" }}>
          These are the failure modes worth designing against before you
          commit the plan.
        </p>
        <ul className="reveal checks" style={{ "--delay": "400ms" }}>
          <li>
            ✅ <strong>Counting gross output instead of net.</strong> An agent
            that produces 500 items of which 150 need rework has not added 500
            units of capacity. Escalation rate is the whole number.
          </li>
          <li>
            ✅ <strong>Forgetting the review queue.</strong> Approval work is
            capacity consumed. Teams that skip this line discover it as a
            bottleneck on one senior person who is now the constraint on
            everything.
          </li>
          <li>
            ✅ <strong>Letting agent spend float free of outcomes.</strong> If
            compute cost sits in a platform line item, nobody can defend it and
            nobody can cut it intelligently. Attach every dollar to the work it
            funded.
          </li>
          <li>
            ✅ <strong>Governing every agent the same way.</strong> Gartner has
            warned that{" "}
            <a
              href="https://www.gartner.com/en/newsroom/press-releases/2026-05-26-gartner-says-applying-uniform-governance-across-ai-agents-will-lead-to-enterprise-ai-agent-failure"
              target="_blank"
              rel="noopener noreferrer"
            >
              applying uniform governance across AI agents leads to failure
            </a>
            . A read-only roll-up agent and an agent that reassigns work need
            different approval thresholds, or you will either throttle the safe
            one or under-supervise the risky one.
          </li>
        </ul>
        <p className="reveal" style={{ "--delay": "410ms" }}>
          The cost of getting this wrong is already visible in the market.
          Gartner predicts that{" "}
          <a
            href="https://www.gartner.com/en/newsroom/press-releases/2025-06-25-gartner-predicts-over-40-percent-of-agentic-ai-projects-will-be-canceled-by-end-of-2027"
            target="_blank"
            rel="noopener noreferrer"
          >
            over 40% of agentic AI projects will be canceled by the end of 2027
          </a>
          , citing escalating costs, unclear business value, and inadequate risk
          controls. Those are all planning failures rather than technology
          failures. A project with a measured throughput number, a cost per
          item, and an owner for the review queue is a project that can survive
          a budget review.
        </p>

        <h2 id="bossdrop" className="reveal" style={{ "--delay": "420ms" }}>
          What this looks like in BossDrop
        </h2>
        <p className="reveal" style={{ "--delay": "430ms" }}>
          BossDrop is the agent in the model above: it runs the coordination
          lane. It sits on top of the stack you already use, reads the real
          state of work from tasks, chat, and calendars, and acts with
          approvals and an audit trail. Which means the numbers the model needs
          are a byproduct of using it, not a separate measurement project.
        </p>

        <div className="panel panelDark reveal" style={{ "--delay": "440ms" }}>
          <h3 className="h2">The capacity numbers BossDrop produces</h3>
          <ul className="bullets">
            <li>
              <strong>Items closed per period.</strong> Every roll-up, handoff
              chase, assignment, and schedule is a countable unit of
              coordination work that a person did not have to do.
            </li>
            <li>
              <strong>Escalation rate.</strong> How often the agent stopped and
              asked a human, and for what. This is the number that tells you
              whether throughput is trustworthy.
            </li>
            <li>
              <strong>Review hours generated.</strong> The approval load the
              agent creates, so it lands in the human lane of the plan instead
              of surprising a team lead.
            </li>
            <li>
              <strong>Coordination load recovered.</strong> The share of the
              team&apos;s week that stopped going to status work. See{" "}
              <Link to="/blog/the-new-kpi-time-saved-by-ai">
                the time-saved KPI
              </Link>{" "}
              for how to report this without inflating it.
            </li>
            <li>
              <strong>Per-action audit trail.</strong> What triggered it, what
              changed, and who approved it, which is what makes a wider capacity
              model defensible. The{" "}
              <Link to="/blog/audit-ready-ai-agent-automation">
                audit-ready automation playbook
              </Link>{" "}
              covers the controls.
            </li>
          </ul>
          <p className="muted">
            You can see the mechanics on{" "}
            <Link to="/features">the features page</Link>, the control loop on{" "}
            <Link to="/how-it-works">how it works</Link>, and the cost side on{" "}
            <Link to="/pricing">pricing</Link>. For a per-team rollout,{" "}
            <Link to="/teams">teams</Link> covers the smaller shape and{" "}
            <Link to="/enterprise">enterprise</Link> covers permissions and
            deployment.
          </p>
        </div>

        <h2 id="plan" className="reveal" style={{ "--delay": "450ms" }}>
          What to put in the plan instead of a headcount number
        </h2>
        <p className="reveal" style={{ "--delay": "460ms" }}>
          McKinsey&apos;s{" "}
          <a
            href="https://www.mckinsey.com/capabilities/people-and-organizational-performance/our-insights/hr-monitor"
            target="_blank"
            rel="noopener noreferrer"
          >
            HR Monitor 2026
          </a>
          , based on roughly 1,300 HR professionals and 5,500 employees across
          ten countries, found that only 11% of organizations take a genuinely
          long-term view in workforce planning. Most planning is still
          short-term headcount work. The recommendation in that report is to
          move from counting capacity to planning capability, and the capacity
          model above is how you get there in practice: once you know what
          coordination work software is absorbing, you can see which human
          capabilities the plan actually needs more of.
        </p>
        <p className="reveal" style={{ "--delay": "470ms" }}>
          Adoption is no longer the differentiator. McKinsey&apos;s{" "}
          <a
            href="https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            State of AI survey
          </a>{" "}
          now puts agent scaling at 40% of large organizations, up from 27% the
          prior year. Nearly everyone will have agents next year. The companies
          that get a return will be the ones whose plans could say, in advance,
          how much capacity they expected and where it would go.
        </p>
        <p className="reveal" style={{ "--delay": "480ms" }}>
          So the plan line changes shape. Instead of &quot;add two coordinators,
          &quot; it reads: demand of X items, usable human hours of Y, net agent
          throughput of Z, review reserve of R, with a named owner for the
          escalation queue and a monthly re-baseline. That is a line somebody
          can hold you to, which is the only kind worth writing.
        </p>

        <div className="cta-row reveal" style={{ "--delay": "490ms" }}>
          <a className="btn btnPrimary" {...DEMO_LINK}>
            Model your coordination capacity
          </a>
          <Link className="btn btnGhost" to="/contact-sales">
            Talk to sales
          </Link>
        </div>

        <h2 id="resources" className="reveal" style={{ "--delay": "500ms" }}>
          External resources worth reading
        </h2>
        <ul className="bullets reveal" style={{ "--delay": "510ms" }}>
          <li>
            BCG, AI at Work 2026: adoption, time saved, and the reinvestment gap{" "}
            <a
              href="https://www.bcg.com/publications/2026/ai-at-work-why-strategy-matters-more-than-tools"
              target="_blank"
              rel="noopener noreferrer"
            >
              (bcg.com)
            </a>
          </li>
          <li>
            Microsoft 2025 Work Trend Index on the capacity gap and digital
            labor{" "}
            <a
              href="https://www.microsoft.com/en-us/worklab/work-trend-index/2025-the-year-the-frontier-firm-is-born"
              target="_blank"
              rel="noopener noreferrer"
            >
              (microsoft.com)
            </a>
          </li>
          <li>
            Tempo 2026 State of SPM report, where capacity planning ranks as the
            top barrier to execution{" "}
            <a
              href="https://www.tempo.io/guides/2026-state-of-spm-report-guide"
              target="_blank"
              rel="noopener noreferrer"
            >
              (tempo.io)
            </a>
          </li>
          <li>
            McKinsey HR Monitor 2026 on the shift from capacity planning to
            capability planning{" "}
            <a
              href="https://www.mckinsey.com/capabilities/people-and-organizational-performance/our-insights/hr-monitor"
              target="_blank"
              rel="noopener noreferrer"
            >
              (mckinsey.com)
            </a>
          </li>
          <li>
            Gartner on why over 40% of agentic AI projects are expected to be
            canceled by 2027{" "}
            <a
              href="https://www.gartner.com/en/newsroom/press-releases/2025-06-25-gartner-predicts-over-40-percent-of-agentic-ai-projects-will-be-canceled-by-end-of-2027"
              target="_blank"
              rel="noopener noreferrer"
            >
              (gartner.com)
            </a>
          </li>
          <li>
            Gartner on why uniform governance across agents causes failure{" "}
            <a
              href="https://www.gartner.com/en/newsroom/press-releases/2026-05-26-gartner-says-applying-uniform-governance-across-ai-agents-will-lead-to-enterprise-ai-agent-failure"
              target="_blank"
              rel="noopener noreferrer"
            >
              (gartner.com)
            </a>
          </li>
          <li>
            Asana Anatomy of Work Index on the share of the day lost to
            coordination{" "}
            <a
              href="https://asana.com/resources/anatomy-of-work-index"
              target="_blank"
              rel="noopener noreferrer"
            >
              (asana.com)
            </a>
          </li>
          <li>
            McKinsey State of AI survey on how far agent scaling has actually
            gone{" "}
            <a
              href="https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              (mckinsey.com)
            </a>
          </li>
        </ul>

        <div className="panel reveal" style={{ "--delay": "520ms" }}>
          <h3 className="h2">Related BossDrop reading</h3>
          <ul className="bullets">
            <li>
              <Link to="/blog/span-of-control-agentic-ai">
                Span of Control: Why Middle Management Caps Your Team
              </Link>
            </li>
            <li>
              <Link to="/blog/context-switching-tax">
                The Context-Switching Tax: What Fragmented Attention Costs
              </Link>
            </li>
            <li>
              <Link to="/blog/the-new-kpi-time-saved-by-ai">
                The New KPI: Time Saved by AI
              </Link>
            </li>
            <li>
              <Link to="/compare">
                How BossDrop compares to traditional work management tools
              </Link>
              , including the{" "}
              <Link to="/compare/motion">Motion comparison</Link> for teams
              evaluating AI scheduling.
            </li>
          </ul>
          <p className="muted">
            Span of control explains why coordination caps a team. Capacity
            planning is how you put a number on it.
          </p>
        </div>

        <FAQ
          reveal={false}
          items={[
            {
              q: "What is capacity planning for AI agents?",
              a: "It means sizing the work an agent can actually complete in a period, inside the same plan you use for people. People are sized in usable hours after meetings and unplanned work. Agents are sized in throughput: runs per period times items per run, minus the share that escalates to a human. Both feed one capacity number for the quarter.",
            },
            {
              q: "How do you measure the capacity of an AI agent?",
              a: "Measure three things over two weeks of real use: how many runs the agent completes, how many work items each run closes, and what share of those items a human takes back. Multiply the first two and subtract the escalation share for net throughput. Track cost per completed item alongside it so capacity and spend move together.",
            },
            {
              q: "Should AI agents count as headcount in a workforce plan?",
              a: "No. An agent has no seat, so a headcount line either hides it or turns it into unexplained spend. Plan agents as a second kind of capacity with their own unit, cost driver, and governance requirements, then roll both lanes into a single capacity ledger for the quarter.",
            },
            {
              q: "How do you budget for AI agents?",
              a: "Budget them as variable cost tied to volume rather than fixed cost tied to a seat. Estimate runs per month, multiply by cost per run, and attach that line to the outcome it was funded for. Add a review-time line for the approvals the agent will generate, because that time is real capacity being consumed.",
            },
            {
              q: "How is capacity planning different from workforce planning?",
              a: "Workforce planning looks three to five years out and asks what skills the strategy will need. Capacity planning asks whether the team you have can absorb the demand in front of you this quarter. Agents hit capacity planning first because they can be added in days, and workforce planning later as the skill mix shifts toward review and exception handling.",
            },
            {
              q: "Does adding AI agents mean we stop hiring?",
              a: "Usually not. Agents absorb coordination work: status gathering, routing, follow-up, and handoffs. That frees human capacity rather than removing the need for it. The decision worth making during planning is where the freed hours go, because capacity that is not explicitly reassigned gets absorbed by whatever is loudest.",
            },
          ]}
        />

        <div className="panel reveal" style={{ "--delay": "560ms" }}>
          <div className="kpis">
            <span className="pill">capacity planning</span>
            <span className="pill">blended workforce</span>
            <span className="pill">agent throughput</span>
            <span className="pill">agentic automation</span>
          </div>
          <p className="muted">
            Stop planning seats. Plan the work, and know which part of it is
            software.
          </p>
          <div className="cta-row">
            <a className="btn btnPrimary" {...DEMO_LINK}>
              Book a demo
            </a>
          </div>
        </div>
      </article>
    </section>
  );
}

/* ---------- helpers ---------- */
function setMeta(content) {
  setOrCreate(
    'meta[name="description"]',
    (el) => el.setAttribute("name", "description"),
    content,
  );
}
function setKeywords(content) {
  setOrCreate(
    'meta[name="keywords"]',
    (el) => el.setAttribute("name", "keywords"),
    content,
  );
}
function setOG(property, content) {
  if (!content) return;
  const sel = property.startsWith("og:")
    ? `meta[property="${property}"]`
    : `meta[name="${property}"]`;
  setOrCreate(
    sel,
    (el) => {
      property.startsWith("og:")
        ? el.setAttribute("property", property)
        : el.setAttribute("name", property);
    },
    content,
  );
}
function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}
function setOrCreate(selector, init, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    init(el);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function injectLDJSON(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}
function toAbs(url) {
  if (!url) return "";
  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}

/* --- inline SVGs --- */
function iconX() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18.9 2H22l-7.7 8.8L22 22h-6.3l-4.9-6-5.6 6H2l8.5-9.4L2 2h6.4l4.4 5.4L18.9 2z"
      />
    </svg>
  );
}
function iconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 8h5v16H0V8zm7.5 0h4.8v2.2h.1c.7-1.2 2.3-2.5 4.7-2.5 5 0 5.9 3.3 5.9 7.6V24h-5V16.4c0-1.8 0-4.1-2.5-4.1s-2.9 2-2.9 4v7.7h-5V8z"
      />
    </svg>
  );
}
function iconFacebook() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.7-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12"
      />
    </svg>
  );
}
